import * as Sentry from '@sentry/react';
import { debugEnabled } from '../config';

/**
 * Wraps any error with a stable, searchable name for Sentry grouping.
 * Preserves the original error message and stores the original as `cause`.
 */
export class NamedError extends Error {
	cause: unknown;

	constructor(name: string, cause: unknown) {
		const originalError = cause instanceof Error ? cause : new Error(String(cause));
		super(originalError.message);
		this.name = name;
		this.cause = cause;
		if (originalError.stack) {
			this.stack = originalError.stack;
		}
	}
}

/**
 * Builds a concise, human-readable message from common error-shaped object
 * fields. Returns undefined when no useful fields are present so callers can
 * fall back to other strategies (e.g. JSON.stringify).
 */
function getReadableMessageFromObject(obj: Record<string, unknown>, depth = 0): string | undefined {
	const name = typeof obj.name === 'string' ? obj.name : undefined;
	const detail =
		(typeof obj.description === 'string' && obj.description) ||
		(typeof obj.message === 'string' && obj.message) ||
		(typeof obj.reason === 'string' && obj.reason) ||
		undefined;

	if (name && detail) {
		return `${name}: ${detail}`;
	}
	if (name || detail) {
		return name || detail;
	}

	// Some libraries wrap the meaningful error one level deep under `error` (e.g.
	// media-picker) or the standard `cause`. Recurse into those before giving up so
	// we surface a readable message instead of a raw JSON dump. Bound the depth to
	// avoid cycles / runaway recursion.
	if (depth < 2) {
		for (const key of ['error', 'cause'] as const) {
			const nested = obj[key];
			if (nested && typeof nested === 'object') {
				const nestedMessage = getReadableMessageFromObject(nested as Record<string, unknown>, depth + 1);
				if (nestedMessage) {
					return nestedMessage;
				}
			}
		}
	}

	return undefined;
}

export function normalizeError(error: unknown): Error {
	// If it's already an Error instance, return as is
	if (error instanceof Error) {
		return error;
	}

	// Handle null/undefined explicitly
	if (error === null) {
		return new Error('Error was null');
	}
	if (error === undefined) {
		return new Error('Error was undefined');
	}

	// For objects, prefer a meaningful, human-readable message built from common
	// error-shaped fields (e.g. media-client errors expose name/description/reason)
	// before falling back to a full JSON dump, which produces noisy Sentry titles
	// and poor grouping.
	if (typeof error === 'object') {
		const readableMessage = getReadableMessageFromObject(error as Record<string, unknown>);
		if (readableMessage) {
			return new Error(readableMessage);
		}
		try {
			return new Error(JSON.stringify(error));
		} catch {
			return new Error('[Object cannot be stringified]');
		}
	}

	// For other primitives, convert to string
	return new Error(String(error));
}

/**
 * Logs an info message to both console (if debug enabled) and Sentry.
 *
 * ⚠️ IMPORTANT: Do not log PII (Personally Identifiable Information) such as:
 * - Email addresses, phone numbers, or physical addresses
 * - Full names, usernames, or nicknames
 * - Passwords, tokens, or authentication credentials
 * - Any other sensitive user data as defined in https://hello.atlassian.net/wiki/spaces/PMP/pages/139161585/Policy+-+Data+Classification
 *
 * Ensure all potentially sensitive data is sanitized before logging.
 */
export function logInfo(msg: string, ...args: any[]): void {
	if (debugEnabled) {
		// eslint-disable-next-line no-console
		console.log(msg, ...args);
	}
	Sentry.captureEvent({ message: msg, level: 'info', extra: { ...args } });
}

/**
 * Logs a warning message to both console (if debug enabled) and Sentry.
 *
 * ⚠️ IMPORTANT: Do not log PII (Personally Identifiable Information) such as:
 * - Email addresses, phone numbers, or physical addresses
 * - Full names, usernames, or nicknames
 * - Passwords, tokens, or authentication credentials
 * - Any other sensitive user data as defined in https://hello.atlassian.net/wiki/spaces/PMP/pages/139161585/Policy+-+Data+Classification
 *
 * Ensure all potentially sensitive data is sanitized before logging.
 */
export function logWarn(msg: string, ...args: any[]): void {
	if (debugEnabled) {
		// eslint-disable-next-line no-console
		console.warn(msg, ...args);
	}

	Sentry.captureEvent({ message: msg, level: 'warning', extra: { ...args } });
}

interface LogErrorProps {
	error: unknown;
	impact?: string;
	extra?: Record<string, unknown>;
	tags?: Record<string, string>;
	level?: 'error' | 'warning' | 'info';
	context?: Record<string, Record<string, unknown>>;
}

/**
 * Logs an error to both console (if debug enabled) and Sentry.
 *
 * ⚠️ IMPORTANT: Do not log PII (Personally Identifiable Information) such as:
 * - Email addresses, phone numbers, or physical addresses
 * - Full names, usernames, or nicknames
 * - Passwords, tokens, or authentication credentials
 * - Any other sensitive user data as defined in https://hello.atlassian.net/wiki/spaces/PMP/pages/139161585/Policy+-+Data+Classification
 *
 * Ensure all potentially sensitive data is sanitized before logging.
 */
export function logError({ error, impact, extra, tags, context }: LogErrorProps): void {
	const errorObject = normalizeError(error);

	if (debugEnabled) {
		// eslint-disable-next-line no-console
		console.error(impact, errorObject, extra);
	}

	Sentry.captureException(errorObject, {
		tags: {
			...tags,
		},
		extra: {
			impact,
			...(extra ?? {}),
		},
		contexts: {
			...context,
		},
	});
}
